import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../services/api';

// ---- Dispatcher (office) ----------------------------------------------------
export function useCommTargets() {
  return useQuery({
    queryKey: ['commTargets'],
    queryFn: async () => (await api.get('/api/comms/targets')).data,
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useComms() {
  return useQuery({
    queryKey: ['comms'],
    queryFn: async () => (await api.get('/api/comms')).data.comms,
    staleTime: 0,
    retry: false,
  });
}

export function useComm(id) {
  return useQuery({
    queryKey: ['comm', id],
    queryFn: async () => (await api.get(`/api/comms/${id}`)).data,
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useCreateComm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => (await api.post('/api/comms', payload)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comms'] }),
  });
}

// Edit an already-sent message (content is corrected everywhere it's read).
export function useUpdateComm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }) => (await api.put(`/api/comms/${id}`, payload)).data,
    onSuccess: (_d, { id }) => {
      qc.invalidateQueries({ queryKey: ['comm', id] });
      qc.invalidateQueries({ queryKey: ['comms'] });
      qc.invalidateQueries({ queryKey: ['commInbox'] });
    },
  });
}

export async function fetchCommAnalyticsPdf(id) {
  // Returns { filename, base64 } — base64 so it survives the JSON forwarder.
  const { data } = await api.get(`/api/comms/${id}/analytics.pdf`);
  return data;
}

// Remind: pass { id } to chase all unread, or { id, recipientIds } to chase a
// selected subset.
export function useRemindComm() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, recipientIds }) =>
      (await api.post(`/api/comms/${id}/remind`, recipientIds && recipientIds.length ? { recipientIds } : {})).data,
    onSuccess: (_d, { id }) => { qc.invalidateQueries({ queryKey: ['comm', id] }); qc.invalidateQueries({ queryKey: ['comms'] }); },
  });
}

// Add picked people to an already-sent message (manual "send to new members").
export function useAddRecipients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, entraIds, send_email }) =>
      (await api.post(`/api/comms/${id}/recipients`, { entraIds, send_email })).data,
    onSuccess: (_d, { id }) => { qc.invalidateQueries({ queryKey: ['comm', id] }); qc.invalidateQueries({ queryKey: ['comms'] }); qc.invalidateQueries({ queryKey: ['commNewMembers', id] }); },
  });
}

// Preview how many people have since joined the message's stored audience.
export function useNewMembers(id) {
  return useQuery({
    queryKey: ['commNewMembers', id],
    queryFn: async () => (await api.get(`/api/comms/${id}/new-members`)).data,
    enabled: Boolean(id),
    retry: false,
    staleTime: 0,
  });
}

// Auto-add everyone who now matches the message's stored audience.
export function useAddNewMembers() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/comms/${id}/new-members`)).data,
    onSuccess: (_d, id) => { qc.invalidateQueries({ queryKey: ['comm', id] }); qc.invalidateQueries({ queryKey: ['comms'] }); qc.invalidateQueries({ queryKey: ['commNewMembers', id] }); },
  });
}

// Send a scheduled message now, or withdraw it before it goes out.
function useScheduledAction(action) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/comms/${id}/${action}`, {})).data,
    onSuccess: (_d, id) => { qc.invalidateQueries({ queryKey: ['comm', id] }); qc.invalidateQueries({ queryKey: ['comms'] }); },
  });
}
export const useReleaseComm = () => useScheduledAction('release');
export const useCancelComm = () => useScheduledAction('cancel');
// ---- Recipient inbox --------------------------------------------------------
export function useInbox() {
  return useQuery({
    queryKey: ['commInbox'],
    queryFn: async () => (await api.get('/api/comms/inbox')).data.messages,
    staleTime: 0,
    retry: false,
  });
}

export function useInboxUnread() {
  return useQuery({
    queryKey: ['commInboxUnread'],
    queryFn: async () => (await api.get('/api/comms/inbox/unread-count')).data.unread,
    staleTime: 15 * 1000,
    refetchInterval: 20 * 1000,        // keep the badge live without a manual refresh
    refetchOnWindowFocus: true,
    retry: false,
  });
}

export function useInboxMessage(id) {
  const qc = useQueryClient();
  return useQuery({
    queryKey: ['commInboxMessage', id],
    queryFn: async () => {
      const { data } = await api.get(`/api/comms/inbox/${id}`); // opening marks it read
      qc.invalidateQueries({ queryKey: ['commInbox'] });
      qc.invalidateQueries({ queryKey: ['commInboxUnread'] });
      return data;
    },
    enabled: Boolean(id),
    staleTime: 0,
  });
}

export function useAcknowledge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => (await api.post(`/api/comms/${id}/ack`)).data,
    // Also refresh the open message so its "I acknowledge" button flips to
    // "Acknowledged" immediately (not just after navigating away and back).
    onSuccess: (_d, id) => { qc.invalidateQueries({ queryKey: ['commInbox'] }); qc.invalidateQueries({ queryKey: ['commInboxUnread'] }); qc.invalidateQueries({ queryKey: ['commInboxMessage', id] }); },
  });
}

// ---- Recipient activity (per-user engagement view) --------------------------
export function useRecipients() {
  return useQuery({
    queryKey: ['commRecipients'],
    queryFn: async () => (await api.get('/api/comms/recipients')).data.recipients,
  });
}

export function useRecipientActivity(entraId) {
  return useQuery({
    queryKey: ['commRecipientActivity', entraId],
    queryFn: async () => (await api.get(`/api/comms/recipients/${entraId}`)).data,
    enabled: Boolean(entraId),
  });
}
